import { isDeliverableCatalogItem, isDeliverableItemType } from '@apps/registry/common/components/Catalog/utils/interpretItemType';
import type { TrackAttemptPayload } from './useAddItemToRegistryAttempt';

/**
 * Whether a pending add would put a Joy store item on the registry, which a lean international
 * registry cannot accept. Cash funds, charity funds and the friends-registry redirect can all
 * proceed against an international event.
 *
 * Mirrors the refusal rule `CatalogProviderRoot.addCatalogItemToRegistry` applies once an event is
 * already selected. The two cannot literally share a predicate — this one classifies a
 * `TrackAttemptPayload` captured before selection, that one an `AddRegistryItemPayloadBySource`
 * assembled after it — so they share the `interpretItemType` helpers instead, and "deliverable"
 * means the same thing on both sides: anything that is not a cash or charity fund.
 */
export const isDeliverableAddAttempt = (attempt: TrackAttemptPayload<unknown> | null): boolean => {
  if (!attempt) {
    return false;
  }

  switch (attempt.type) {
    // Redirects to the friends registry instead of adding anything.
    case 'transitionalRegistryBanner':
      return false;
    // An external store's product — never a fund, and refused on the international registry today.
    case 'universalSearch':
      return true;
    // Carries its own item type, and `product` is null for a custom fund, so never read it here.
    case 'catalog':
      return isDeliverableItemType(attempt.itemType);
    case 'item':
      return isDeliverableCatalogItem(attempt.product);
  }
};
